/**
 * Custom OptOut for Granite Wellness
 *
 * Overrides the guard behavior to show a confirmation modal when a user
 * with an existing claim attempts to access the opt-out form, instead of
 * showing a generic error.
 */
import { OptOut } from "commonAssets/components/Forms/OptOut.js";
import { createElement } from "commonAssets/lib/createElement.js";
import { modal } from "commonAssets/lib/modal.js";
import { loadingGif } from "commonAssets/lib/loadingGif.js";
import { validateField } from "commonAssets/lib/validateField.js";
import { clearMessage } from "commonAssets/lib/clearMessage.js";
import { toggleFieldError } from "commonAssets/lib/toggleFieldError.js";
import { loadMember } from "commonAssets/lib/loadMember.js";
import { i18n, loading, outputDiv } from "commonAssets/js/constants";
import { ErrorHandler } from "commonAssets/utils/ErrorHandler";

export class GraniteOptOut extends OptOut {
    /**
     * Creates an instance of GraniteOptOut.
     */
    static createInstance({
        appClaimForm,
        userLanguage,
        i18n,
        enableClaimFields = false,
        enableClaimTransactions = false,
        errorHandler = new ErrorHandler(),
    }) {
        return new GraniteOptOut(
            appClaimForm,
            userLanguage,
            i18n,
            enableClaimFields,
            enableClaimTransactions,
            errorHandler
        );
    }

    init_exclusion_guard(form, container, datetime, exclusion_deadline) {
        // Guard setup (same as parent)
        container.appendChild(this.guard(form, datetime));
        outputDiv.appendChild(container);

        const zero_zero_id = document.getElementById("zero_zero_id");
        const confirmation_code = document.getElementById("confirmation_code");
        const skip_guard = document.getElementById("skip-guard");

        [zero_zero_id, confirmation_code].forEach((input) => {
            input.addEventListener("change", validateField);
        });

        if (skip_guard) {
            skip_guard.addEventListener("click", () => {
                document.getElementById("guard").classList.add("d-none");
                container.appendChild(this.exclusion_form(form));
                container.scrollIntoView({ behavior: "smooth" });
                outputDiv.appendChild(container);

                // Override contact information title for opt-out form (remove section number prefix)
                const contactInfoTitle = document.getElementById("contactInfoTitle");
                if (contactInfoTitle) {
                    contactInfoTitle.innerHTML = "CLASS MEMBER NAME AND CONTACT INFORMATION";
                }

                this.init_exclusion_fields();
                document
                    .getElementById("form")
                    .addEventListener("submit", (process) => {
                        this.process_exclusion(
                            process,
                            this.exclusion,
                            exclusion_deadline
                        );
                    });
            });
        }

        // Custom guard submit handler with claim_id confirmation flow
        document.getElementById("guard").addEventListener("submit", (e) => {
            e.preventDefault();

            let valid = true;
            const member = {
                domain: this.claim.data.domain,
                zero_zero_id: zero_zero_id.value.toUpperCase(),
                confirmation_code: confirmation_code.value,
            };

            clearMessage();

            [zero_zero_id, confirmation_code].forEach((input) => {
                valid = validateField(input) && valid;
            });
            if (!valid) return;

            loadingGif(loading, "show");

            this.claim.api
                .classMemberPost({}, member)
                .then((result) => {
                    // CUSTOM: Handle existing claim scenario with confirmation modal
                    // Check for claim_id regardless of error status
                    if (result.data.claim_id) {
                        loadingGif(loading, "hide", 250);
                        this._showClaimConfirmationModal(
                            result,
                            member,
                            form,
                            container,
                            exclusion_deadline
                        );
                        return;
                    }

                    // Standard error handling (no claim_id)
                    if (result.data.error) {
                        toggleFieldError(zero_zero_id, true, "");
                        toggleFieldError(confirmation_code, true, "");
                        this.errorHandler.log_exception(
                            new Error("Error fetching class member", {
                                cause: result,
                            })
                        );
                        this.errorHandler.display_modal(result);
                        return;
                    }

                    // Success - proceed to form
                    this._proceedToExclusionForm(
                        result,
                        member,
                        form,
                        container,
                        exclusion_deadline
                    );
                })
                .catch((error) => {
                    this.errorHandler.log_exception(
                        new Error("Error fetching class member", {
                            cause: error,
                        })
                    );
                    this.errorHandler.display_modal(error);
                });
        });
    }

    /**
     * Shows confirmation modal for users with existing claims
     */
    _showClaimConfirmationModal(
        result,
        member,
        form,
        container,
        exclusion_deadline
    ) {
        const continueBtn = createElement(
            "button",
            {
                type: "button",
                class: "btn btn-primary",
            },
            [],
            "Continue"
        );

        modal(
            "Notice",
            "This Notice ID may have already been used to submit a Claim. If you wish to file an Opt-Out, please click Continue.",
            [continueBtn]
        );

        continueBtn.addEventListener("click", () => {
            // Close modal
            const modalEl = continueBtn.closest(".modal");
            if (modalEl) {
                const bsModal = window.bootstrap?.Modal?.getInstance(modalEl);
                if (bsModal) bsModal.hide();
                else modalEl.remove();
            }

            // Proceed to form
            this._proceedToExclusionForm(
                result,
                member,
                form,
                container,
                exclusion_deadline
            );
        });
    }

    /**
     * Common logic to proceed to exclusion form after guard validation
     */
    _proceedToExclusionForm(result, member, form, container, exclusion_deadline) {
        this.exclusion.data.zero_zero_id = member.zero_zero_id;
        this.exclusion.data.confirmation_code = member.confirmation_code;

        if (result.data.claim_id) {
            this.exclusion.data.claim_id = result.data.claim_id;
        }

        document.getElementById("guard").classList.add("d-none");
        container.appendChild(this.exclusion_form(form));
        container.scrollIntoView({ behavior: "smooth" });
        outputDiv.appendChild(container);

        // Override contact information title for opt-out form (remove section number prefix)
        const contactInfoTitle = document.getElementById("contactInfoTitle");
        if (contactInfoTitle) {
            contactInfoTitle.innerHTML = "CLASS MEMBER NAME AND CONTACT INFORMATION";
        }

        loadingGif(loading, "hide", 250);

        loadMember(result.data);
        this.init_exclusion_fields(this.exclusion, form);

        document.getElementById("form").addEventListener("submit", (process) => {
            this.process_exclusion(process, this.exclusion, exclusion_deadline);
        });
    }
}
